/**
 * Registration Hook
 * Hook لإدارة عملية التسجيل الموحدة
 */

import { useState, useEffect } from 'react';
import {
  UnifiedRegistrationData,
  ParentRegistrationData,
  UserType,
  ValidationErrors,
  RegistrationResult,
  STUDENT_CODE_PATTERN,
} from '../types/registration';
import {
  registerUnifiedUser,
  validateRegistrationData,
} from '../services/registrationService';
import SimpleCodeValidation from '../services/simpleCodeValidation';

interface CodeValidation {
  isChecking: boolean;
  isValid: boolean;
  studentName?: string;
  studentGrade?: string;
  studentId?: string;
  error?: string;
  confirmationMessage?: string;
}

export const useRegistration = () => {
  const [currentStep, setCurrentStep] = useState<
    'userType' | 'form' | 'success'
  >('userType');
  const [selectedUserType, setSelectedUserType] = useState<UserType | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [registrationResult, setRegistrationResult] =
    useState<RegistrationResult | null>(null);

  const [formData, setFormData] = useState<Partial<UnifiedRegistrationData>>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    userType: 'student',
    acceptTerms: false,
  });

  const [codeValidation, setCodeValidation] = useState<CodeValidation>({
    isChecking: false,
    isValid: false,
  });
  const studentCode =
    selectedUserType === 'parent'
      ? (formData as ParentRegistrationData).studentCode || ''
      : '';

  // تحديث بيانات النموذج
  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // إزالة الخطأ عند تعديل الحقل
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // التحقق من صحة كود الطالب
  const checkStudentCode = async (code: string) => {
    const normalizedCode = code.trim().toUpperCase();

    if (!normalizedCode) {
      setCodeValidation({ isChecking: false, isValid: false });
      return;
    }

    if (!STUDENT_CODE_PATTERN.test(normalizedCode)) {
      setCodeValidation({
        isChecking: false,
        isValid: false,
        error: 'كود الطالب يجب أن يكون بصيغة S-XXXXXXXX',
      });
      return;
    }

    setCodeValidation({ isChecking: true, isValid: false });

    try {
      // استخدام النظام البسيط للتحقق من الكود
      const result = await SimpleCodeValidation.validateStudentCode(
        normalizedCode
      );

      if (result.isValid) {
        const studentData = result.studentData;

        // استخراج البيانات من مصادر مختلفة
        const name =
          studentData?.name || studentData?.displayName || 'طالب غير محدد';
        const grade =
          studentData?.grade ||
          studentData?.studentProfile?.grade ||
          studentData?.class ||
          'غير محدد';

        setCodeValidation({
          isChecking: false,
          isValid: true,
          studentName: name,
          studentGrade: grade,
          studentId: studentData?.id,
          confirmationMessage: `بيانات الطالب: ${name} - الصف: ${grade}`,
        });
      } else {
        setCodeValidation({
          isChecking: false,
          isValid: false,
          error: result.error || 'الكود غير صحيح أو غير موجود',
        });
      }
    } catch (error) {
      setCodeValidation({
        isChecking: false,
        isValid: false,
        error: 'فشل في التحقق من الكود',
      });
    }
  };

  // تأثير للتحقق من كود الطالب
  useEffect(() => {
    if (selectedUserType === 'parent') {
      if (studentCode) {
        const timeoutId = setTimeout(() => {
          checkStudentCode(studentCode);
        }, 500);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [selectedUserType, studentCode]);

  // اختيار نوع المستخدم
  const handleUserTypeSelection = (userType: UserType) => {
    setSelectedUserType(userType);
    setFormData(prev => ({ ...prev, userType }));
    setCurrentStep('form');
  };

  // إرسال النموذج
  const handleSubmit = async (): Promise<void> => {
    if (!selectedUserType) return;

    // التحقق من صحة البيانات
    const errors = validateRegistrationData(
      formData as UnifiedRegistrationData
    );
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await registerUnifiedUser(
        formData as UnifiedRegistrationData
      );
      setRegistrationResult(result);

      if (result.success) {
        setCurrentStep('success');
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // العودة للخطوة السابقة
  const goBack = () => {
    if (currentStep === 'form') {
      setCurrentStep('userType');
      setSelectedUserType(null);
    }
  };

  // إعادة تعيين النموذج
  const resetForm = () => {
    setCurrentStep('userType');
    setSelectedUserType(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      userType: 'student',
      acceptTerms: false,
    });
    setValidationErrors({});
    setRegistrationResult(null);
    setCodeValidation({ isChecking: false, isValid: false });
  };

  return {
    // الحالات
    currentStep,
    selectedUserType,
    isLoading,
    validationErrors,
    registrationResult,
    formData,
    codeValidation,

    // الوظائف
    updateFormData,
    handleUserTypeSelection,
    handleSubmit,
    goBack,
    resetForm,
  };
};
